/**
 * TanStack Query hooks and key factory for the DAE dashboard.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import { daeApiService } from "@/services/daeApi"
import { ingestionApiService } from "@/services/ingestionApi"
import { sdk, sdkCall } from "@/services/sdk"

import type { DashboardStats } from "@/services/daeApi"

const dashboardKeys = {
  all: ["dashboard"] as const,
  stats: (daeId: string | undefined) => [...dashboardKeys.all, "stats", daeId ?? null] as const,
  needsAttention: (daeId: string | undefined) =>
    [...dashboardKeys.all, "needs-attention", daeId ?? null] as const,
  insights: () => [...dashboardKeys.all, "insights"] as const,
}

/**
 * Fetches statistics from the server-side aggregation endpoint. Single call replaces N+1
 * client-side aggregation.
 */
export function useDashboardStats(daeId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.stats(daeId),
    queryFn: async () => {
      const data = await sdkCall(sdk.getStatistics())
      return {
        studies: {
          total: data.studies.total,
          active: data.studies.active,
          health: {
            atRisk: data.studies.health.atRisk,
            watch: data.studies.health.watch,
            healthy: data.studies.health.healthy,
          },
        },
        dtas: {
          total: data.dtas.total,
          active: data.dtas.active,
          pendingReviews: data.dtas.pendingReviews,
          pendingValidations: data.dtas.pendingValidations,
        },
        vendors: { active: data.vendors.active },
      } satisfies DashboardStats
    },
    enabled: !!daeId,
  })
}

/**
 * Fetches a map of study ID → attention summary for studies that need attention — i.e. have a
 * failed / partially-completed ingestion run or quarantined (validation-failed) records. Derived
 * live, this is the single source shared by the dashboard "Need Attention" tile (map size) and the
 * My Studies "Needs Attention" filter + inline hint (per-study counts) — one definition feeding
 * both. The tile counts across all studies; the My Studies filtered rows converge to it once that
 * page has loaded every study page.
 */
export function useStudiesNeedingAttention(daeId: string | undefined) {
  return useQuery({
    queryKey: dashboardKeys.needsAttention(daeId),
    queryFn: () => daeApiService.getStudiesNeedingAttention(),
    enabled: !!daeId,
  })
}

/**
 * Fetches AI-generated insights with server-side pagination (25 per page). Uses infinite query for
 * scroll-to-load in the VerifAI panel.
 */
export function useInsights() {
  return useInfiniteQuery({
    queryKey: dashboardKeys.insights(),
    queryFn: ({ pageParam }) => ingestionApiService.getInsights(undefined, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? null,
  })
}
